import React from "react";

// Re-usable iPhone bezel with notch / Dynamic Island.
// Children render inside the screen area (375x780 viewBox available).
export function PhoneFrame({ children, className = "", glow = true }) {
  return (
    <div
      className={`relative mx-auto ${className}`}
      style={{ width: "min(320px, 100%)" }}
      data-testid="phone-frame"
    >
      <div
        className="relative rounded-[44px] p-[10px] hairline"
        style={{
          background: "linear-gradient(180deg,#1a1d24,#0a0c10)",
          boxShadow: glow
            ? "0 30px 80px rgba(0,0,0,0.6), 0 0 60px rgba(0,255,136,0.08)"
            : "0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div
          className="relative overflow-hidden rounded-[36px]"
          style={{ background: "#0B0F14" }}
        >
          {/* Notch / Dynamic Island */}
          <div className="absolute left-1/2 top-2 z-20 h-6 w-24 -translate-x-1/2 rounded-full bg-black" />
          <div className="aspect-[9/19.5] w-full">
            <div className="flex h-full flex-col">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Status Bar -----
export function StatusBar({ time = "09:42" }) {
  return (
    <div className="flex items-center justify-between px-6 pb-1 pt-3 text-[11px] mono-num text-ink">
      <span>{time}</span>
      <span className="flex items-center gap-1">
        <span className="inline-block h-1 w-1 rounded-full bg-primary" />
        <span className="inline-block h-1 w-1 rounded-full bg-primary" />
        <span className="inline-block h-1 w-1 rounded-full bg-primary" />
        <span className="inline-block h-1 w-1 rounded-full bg-ink/40" />
        <span className="ml-2 text-[10px] tracking-widest text-ink/60">5G</span>
        <span className="ml-2 inline-block h-2 w-4 rounded-sm border border-ink/40" />
      </span>
    </div>
  );
}

// ----- Dashboard Mock -----
export function DashboardMock({ t }) {
  return (
    <div className="flex h-full flex-col">
      <StatusBar />
      <div className="flex items-center justify-between px-5 pt-2">
        <div>
          <p className="label-tag text-[10px]">{t.bar}</p>
          <p className="font-display text-[15px] font-semibold text-ink">
            Olá, Operador.
          </p>
        </div>
        <div className="h-9 w-9 rounded-full hairline grid place-items-center bg-surface">
          <span className="mono-num text-[10px] text-primary">G</span>
        </div>
      </div>

      {/* points + streak */}
      <div className="mx-5 mt-4 rounded-2xl p-4 hairline relative overflow-hidden"
           style={{ background: "linear-gradient(180deg,#1C1F26,#15181f)" }}>
        <p className="label-tag text-[9px]">{t.points}</p>
        <p className="mono-num text-3xl text-ink leading-tight mt-1">2,840</p>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <p className="label-tag text-[9px]">{t.streak}</p>
            <p className="mono-num text-2xl neon-text leading-none">+24</p>
          </div>
          <div className="h-8 w-20 relative">
            <svg viewBox="0 0 80 32" className="h-full w-full">
              <polyline
                fill="none"
                stroke="#00FF88"
                strokeWidth="1.5"
                points="0,28 10,22 20,24 30,18 40,16 50,12 60,14 70,8 78,4"
              />
              <polyline
                fill="rgba(0,255,136,0.12)"
                stroke="none"
                points="0,28 10,22 20,24 30,18 40,16 50,12 60,14 70,8 78,4 78,32 0,32"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* active goal */}
      <div className="mx-5 mt-3 rounded-2xl p-4 hairline">
        <div className="flex items-center justify-between">
          <p className="label-tag text-[9px]">{t.goal}</p>
          <span className="mono-num text-[10px] text-primary">70%</span>
        </div>
        <p className="text-ink text-sm mt-1 font-medium">{t.study} · 60min</p>
        <div className="mt-2 h-1.5 w-full rounded-full bg-surface-2 overflow-hidden">
          <div className="h-full bg-primary" style={{ width: "70%" }} />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <p className="mono-num text-[10px] text-ink/60">42 / 60 MIN</p>
          <p className="mono-num text-[10px] text-ink/40">
            {t.unlock} 18:00
          </p>
        </div>
      </div>

      {/* league */}
      <div className="mx-5 mt-3 rounded-2xl p-4 hairline flex items-center gap-3">
        <div className="h-10 w-10 rounded-md grid place-items-center hairline"
             style={{ background: "rgba(255,184,0,0.08)" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M6 4h12l-1 7a5 5 0 1 1-10 0L6 4Z" stroke="#FFB800" strokeWidth="1.5"/>
            <path d="M9 18h6v3H9z" stroke="#FFB800" strokeWidth="1.5"/>
          </svg>
        </div>
        <div className="flex-1">
          <p className="label-tag text-[9px]">{t.league}</p>
          <p className="mono-num text-xs text-ink mt-0.5">+128 pts hoje</p>
        </div>
        <span className="mono-num text-[10px] text-warning">▲ 2</span>
      </div>

      <div className="flex-1" />

      {/* tab bar */}
      <div className="mx-5 mb-5 mt-4 flex items-center justify-between rounded-2xl px-5 py-3 hairline" style={{ background: "rgba(28,31,38,0.7)" }}>
        {["HOME","META","LIGA","PERFIL"].map((label, i) => (
          <span key={label} className={`label-tag text-[9px] ${i === 0 ? "neon-text" : ""}`}>{label}</span>
        ))}
      </div>
    </div>
  );
}

// ----- Gate / Block Mock -----
export function GateMock() {
  return (
    <div className="flex h-full flex-col"
      style={{ background: "radial-gradient(circle at 50% 30%, rgba(255,59,59,0.18), transparent 60%), #0B0F14" }}>
      <StatusBar />
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="relative mb-4">
          <div className="h-20 w-20 rounded-2xl hairline grid place-items-center"
               style={{ background: "rgba(255,59,59,0.08)", boxShadow: "0 0 40px rgba(255,59,59,0.25)" }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="10" width="16" height="11" rx="2" stroke="#FF3B3B" strokeWidth="1.8"/>
              <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#FF3B3B" strokeWidth="1.8"/>
              <circle cx="12" cy="15" r="1.4" fill="#FF3B3B"/>
            </svg>
          </div>
          <span className="absolute -inset-2 rounded-3xl border border-danger/30 animate-pulse-glow" />
        </div>
        <p className="label-tag text-[10px] text-danger">INSTAGRAM // BLOQUEADO</p>
        <h3 className="font-display text-2xl font-semibold text-ink mt-3 leading-tight">
          Cumpra a meta.<br/>Depois você scrolla.
        </h3>
        <p className="text-ink/60 text-xs mt-3 max-w-[230px]">
          Bloqueio ativo até você completar 60min de estudo profundo.
        </p>

        <div className="mt-6 w-full rounded-2xl p-4 hairline" style={{ background: "rgba(28,31,38,0.7)" }}>
          <div className="flex items-center justify-between">
            <p className="label-tag text-[9px]">DESBLOQUEIO EM</p>
            <p className="mono-num text-[10px] text-primary">42:00</p>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-surface-2 overflow-hidden">
            <div className="h-full bg-danger" style={{ width: "30%" }} />
          </div>
        </div>

        <div className="mt-4 w-full grid grid-cols-2 gap-2">
          <button className="rounded-xl py-2.5 hairline mono-num text-[11px] text-ink/60">
            BYPASS · 1/1
          </button>
          <button className="rounded-xl py-2.5 mono-num text-[11px] text-bg"
                  style={{ background: "#00FF88" }}>
            ABRIR META
          </button>
        </div>
      </div>
    </div>
  );
}

// ----- League / Leaderboard Mock -----
export function LeagueMock() {
  const rows = [
    { r: 1, n: "Lucas M.",  pts: 4820, you: false, badge: "🥇" },
    { r: 2, n: "Renata S.", pts: 4610, you: false, badge: "🥈" },
    { r: 3, n: "André P.",  pts: 4420, you: false, badge: "🥉" },
    { r: 12, n: "Você",     pts: 2840, you: true,  badge: "" },
    { r: 13, n: "Diana K.", pts: 2790, you: false, badge: "" },
  ];
  return (
    <div className="flex h-full flex-col">
      <StatusBar />
      <div className="px-5 pt-2 pb-3 flex items-center justify-between">
        <div>
          <p className="label-tag text-[10px]">LIGA OURO // SEMANA 4</p>
          <p className="font-display text-[15px] font-semibold text-ink">Top operadores</p>
        </div>
        <span className="mono-num text-[10px] text-warning">▲ 2</span>
      </div>

      <div className="mx-5 rounded-2xl hairline overflow-hidden">
        {rows.map((row, i) => (
          <div
            key={row.r}
            className={`flex items-center justify-between px-4 py-3 ${i !== rows.length - 1 ? "border-b" : ""}`}
            style={{
              borderColor: "rgba(245,247,250,0.06)",
              background: row.you ? "rgba(0,255,136,0.06)" : "transparent",
            }}
          >
            <div className="flex items-center gap-3">
              <span className={`mono-num text-[11px] w-6 ${row.you ? "neon-text" : "text-ink/60"}`}>
                #{row.r.toString().padStart(2,"0")}
              </span>
              <div className="h-7 w-7 rounded-full hairline bg-surface-2 grid place-items-center">
                <span className="mono-num text-[10px] text-ink/60">{row.n[0]}</span>
              </div>
              <span className={`text-xs ${row.you ? "text-primary font-semibold" : "text-ink"}`}>
                {row.n} {row.badge}
              </span>
            </div>
            <span className="mono-num text-[11px] text-ink/80">{row.pts.toLocaleString()}</span>
          </div>
        ))}
      </div>

      <div className="mx-5 mt-3 rounded-2xl hairline p-4" style={{ background: "rgba(0,255,136,0.04)" }}>
        <p className="label-tag text-[9px]">PRÓXIMA LIGA</p>
        <p className="text-xs text-ink mt-1">Faltam <span className="mono-num neon-text">1,580</span> pts para Liga Diamante</p>
        <div className="mt-2 h-1.5 w-full rounded-full bg-surface-2 overflow-hidden">
          <div className="h-full bg-primary" style={{ width: "62%" }} />
        </div>
      </div>

      <div className="flex-1" />
      <div className="mx-5 mb-5 mt-4 flex items-center justify-between rounded-2xl px-5 py-3 hairline" style={{ background: "rgba(28,31,38,0.7)" }}>
        {["HOME","META","LIGA","PERFIL"].map((label, i) => (
          <span key={label} className={`label-tag text-[9px] ${i === 2 ? "neon-text" : ""}`}>{label}</span>
        ))}
      </div>
    </div>
  );
}
